
import  { useHistory, Link } from 'react-router-dom';
import {useEffect, useState} from "react"
import {Course} from "models/model"
import {CourseGetPaging, CourseDelete} from "services/courseService"
import {useDispatch, useSelector} from 'react-redux'
import {AppDispatch, RootState} from 'store'
import {fetchLicenseClasses} from 'store/licenseSlice'
import ConfirmDialog from "popup/ConfirmDialog"
import CustomDatePicker from "components/DatePickerVi"
import {toast} from "react-hot-toast"
import {formatDate} from "lib/utils";
import LoadingOverlay from "components/Static/LoadingOverlay";
import Pagination from "components/Pagination";
import { Edit, Plus, Trash } from 'lucide-react';

export default function CoursesPage() {
    const router = useHistory()
    const dispatch: AppDispatch = useDispatch()

    const licenseClasses = useSelector((state: RootState) => state.licenseClasses.items)
    const licenseLoading = useSelector((state: RootState) => state.licenseClasses.loading)

    const [courses, setCourses] = useState<Course[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [searchText, setSearchText] = useState<string>("")
    const [searchLicenseClass, setSearchLicenseClass] = useState<string>("")
    const [isConfirmOpen, setIsConfirmOpen] = useState(false)
    const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null)
    const [startDate, setStartDate] = useState<Date | null>()
    const [endDate, setEndDate] = useState<Date | null>(null)
    const [pagination, setPagination] = useState({
        pageIndex: 1,
        pageSize: 10,
        totalRecords: 0,
        totalPages: 1
    })

    const handleSearch = () => {
        // Reset to first page when searching
        fetchCourses(1)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const openConfirm = (courseId: string) => {
        setSelectedCourseId(courseId)
        setIsConfirmOpen(true)
    }

    const handleDelete = async () => {
        if (selectedCourseId === null) return
        try {
            await CourseDelete(selectedCourseId)
            setCourses(prev => prev.filter(c => c.id !== selectedCourseId))
            toast.success("Xóa khóa học thành công")
            // Refresh pagination after deletion
            fetchCourses(pagination.pageIndex, true)
        } catch (error: any) {
            toast.error(`Xóa khóa học thất bại: ${error.message || "Có lỗi xảy ra"}`)
        } finally {
            setIsConfirmOpen(false)
        }
    }

    const fetchCourses = async (pageIndex: number, keepPageIndex: boolean = false) => {
        setIsLoading(true)
        try {
            const res = await CourseGetPaging({
                pageIndex: keepPageIndex ? pagination.pageIndex : pageIndex,
                pageSize: pagination.pageSize,
                freeTextSearch: searchText,
                licenseClassId: searchLicenseClass || null,
                startDate: startDate ? startDate.toISOString() : null,
                endDate: endDate ? endDate.toISOString() : null,
            })
            setCourses(res.result.items)
            setPagination(prev => ({
                ...prev,
                pageIndex: keepPageIndex ? prev.pageIndex : pageIndex,
                totalRecords: res.result.totalRecord,
                totalPages: Math.ceil(res.result.totalRecord / pagination.pageSize)
            }))
        } catch (error) {
            toast.error('Lỗi khi lấy danh sách khóa học: ' + error.message)
        } finally {
            setIsLoading(false)
        }
    }

    const handlePageChange = (newPageIndex: number) => {
        if (newPageIndex < 1 || newPageIndex > pagination.totalPages) return
        fetchCourses(newPageIndex)
    }

    const handleDateChange = (type: 'start' | 'end') => (date: Date | null) => {
        if (type === 'start') {
            setStartDate(date)
            // If new start date > current end date, reset end date
            if (date && endDate && date > endDate) {
                setEndDate(null)
            }
        } else {
            setEndDate(date)
        }
        // Reset to first page when date changes
        fetchCourses(1)
    }

    useEffect(() => {
        if (licenseClasses.length === 0) {
            dispatch(fetchLicenseClasses())
        }
        fetchCourses(1)
    }, [searchLicenseClass])

    return (
        <div className="mx-auto">
            <div className="grid grid-cols-1 gap-8">
                <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">Quản lý Khóa học</h1>

                    <div className="flex justify-between items-center mb-6">
                        <Link
                            to="/courses/new"
                            className="flex items-center px-3 py-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm text-base"
                        >
                            <Plus className="mr-2"/>
                            <span>Thêm khóa học mới</span>
                        </Link>
                    </div>

                    <div className="mb-6">
                        <div className="md:flex items-end gap-3">
                            <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3 ">
                                <div className="space-x-1">
                                    <label className="text-xs text-gray-500 block">Từ ngày</label>
                                    <CustomDatePicker
                                        // @ts-ignore
                                        selected={startDate}
                                        onChange={handleDateChange('start')}
                                        placeholder="Từ"
                                        id="startDate"
                                        name="startDate"
                                    />
                                </div>
                                <div className="space-x-1">
                                    <label className="text-xs text-gray-500 block">Đến ngày</label>
                                    <CustomDatePicker
                                        // @ts-ignore
                                        selected={endDate}
                                        onChange={handleDateChange('end')}
                                        placeholder="Đến"
                                        id="endDate"
                                        name="endDate"
                                        className="h-[38px]"
                                    />
                                </div>
                                <div className="space-x-1">
                                    <label className="text-xs text-gray-500 block">Hạng</label>
                                    <select
                                        className="md:min-w-max w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                                        value={searchLicenseClass}
                                        onChange={(e) => setSearchLicenseClass(e.target.value)}
                                    >
                                        <option value="">Tất cả</option>
                                        {licenseClasses.map((lc) => (
                                            <option key={lc.id} value={lc.id}>
                                                {lc.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-x-1">
                                    <label className="text-xs text-gray-500 block">Tìm kiếm</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            placeholder="Tìm khóa..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            className="w-100 px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                                        />
                                        <button
                                            onClick={handleSearch}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 "
                                        >
                                            Tìm
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* Table Section */}
                    <div className="w-full overflow-x-auto">
                        <table className="min-w-max w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">STT</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tên
                                    khóa học
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hạng</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Thời
                                    gian
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Số
                                    HV
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Số
                                    buổi
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Thao
                                    tác
                                </th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {courses.map((course, index) => (
                                <tr key={course.id} className={'hover:bg-gray-50 transition-colors'}>
                                    <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-500  cursor-pointer"
                                        onClick={() => router.push(`/courses/schedule/${course.id}`)}>
                                        {(pagination.pageIndex - 1) * pagination.pageSize + index + 1}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap cursor-pointer"
                                        onClick={() => router.push(`/courses/schedule/${course.id}`)}>
                                        <div
                                            className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-sm md:text-base"
                                        >
                                            {course.name}
                                        </div>
                                        <div className="text-xs md:text-sm text-gray-500">{course.classCode}</div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
                                        onClick={() => router.push(`/courses/schedule/${course.id}`)}>
                                        {course.licenseClassName}
                                    </td>
                                    <td
                                        className="px-4 py-3 whitespace-nowrap cursor-pointer"
                                        onClick={() => router.push(`/courses/schedule/${course.id}`)}
                                    >
                                        <div className="flex flex-col space-y-1.5 text-xs md:text-sm text-gray-700">
                                            {course.openingDate && (
                                                <div className="flex items-center">
                                                    <span
                                                        className="w-20 md:w-24 font-medium text-gray-500">Khai giảng:</span>
                                                    <span
                                                        className="font-medium">{formatDate(course.openingDate)}</span>
                                                </div>
                                            )}

                                            {course.registrationDate && (
                                                <div className="flex items-center">
                                                    <span
                                                        className="w-20 md:w-24 font-medium text-gray-500">Đăng ký SH:</span>
                                                    <span
                                                        className="font-medium">{formatDate(course.registrationDate)}</span>
                                                </div>
                                            )}

                                            {course.closingDate && (
                                                <div className="flex items-center">
                                                    <span
                                                        className="w-20 md:w-24 font-medium text-gray-500">Bế giảng:</span>
                                                    <span
                                                        className="font-medium">{formatDate(course.closingDate)}</span>
                                                </div>
                                            )}

                                            <div className="flex items-center">
                                                <span
                                                    className="w-20 md:w-24 font-medium text-gray-500">Thời gian:
                                                </span>
                                                <span className="font-medium">
                                                    {course.startDate ? formatDate(course.startDate) : 'Chưa xác định'}
                                                    <span className="mx-1 text-gray-400">→</span>
                                                    {course.endDate ? formatDate(course.endDate) : 'Chưa xác định'}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
                                        onClick={() => router.push(`/courses/schedule/${course.id}`)}>
                                        {course.totalStudents || 0}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
                                        onClick={() => router.push(`/courses/schedule/${course.id}`)}>
                                        {course.totalSchedules || 0}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-medium space-x-2">
                                        <button
                                            onClick={() => router.push(`/courses/${course.id}/edit`)}
                                            className="text-blue-600 hover:text-blue-900"
                                            title="Sửa"
                                        >
                                            <Edit className="inline"/> Sửa
                                        </button>
                                        <button
                                            onClick={() => openConfirm(course.id!)}
                                            className="text-red-600 hover:text-red-900"
                                            title="Xóa"
                                        >
                                            <Trash className="inline"/> Xóa
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Pagination */}
            <Pagination
                pagination={pagination}
                isLoading={isLoading}
                onPageChange={handlePageChange}
                label="khóa học"
            />

            {isLoading && <LoadingOverlay message="Đang tải dữ liệu..."/>}

            {!isLoading && courses.length === 0 && (
                <div className="mt-4 text-center text-gray-500">
                    Không tìm thấy khóa học nào
                </div>
            )}

            <ConfirmDialog
                isOpen={isConfirmOpen}
                title="Xác nhận xóa"
                description="Bạn có chắc chắn muốn xóa khóa học này không?"
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={handleDelete}
            />
        </div>
    )
}